import React, { useState, useEffect } from 'react';
import { normalizePrompts } from './formatBuilderUtils';

const AI_MODELS = [
  { value: 'gpt-4o-2024-08-06', label: 'GPT-4o (Aug 2024 - Stable)' },
  { value: 'gpt-4o', label: 'GPT-4o (Latest)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster)' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Legacy)' },
];

const FormatPromptsEditor = ({ prompts: initialPrompts, regions, columns, onSave }) => {
  const [prompts, setPrompts] = useState(() => normalizePrompts(initialPrompts));
  const [dirty, setDirty] = useState(false);

  // Keep in sync if parent reloads format (e.g. after save elsewhere)
  useEffect(() => {
    if (!dirty) setPrompts(normalizePrompts(initialPrompts));
  }, [initialPrompts, dirty]);

  const safePrompts = Array.isArray(prompts) ? prompts : normalizePrompts(prompts);
  const safeRegions = Array.isArray(regions) ? regions : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  const handleAdd = () => {
    setPrompts(prev => {
      const list = Array.isArray(prev) ? prev : normalizePrompts(prev);
      return [...list, {
        region_id: safeRegions.length > 0 ? safeRegions[0].id : null,
        prompt_template: '',
        ai_model: 'gpt-4o-2024-08-06',
        use_auto_format: true,
      }];
    });
    setDirty(true);
  };

  const handleChange = (index, key, value) => {
    setPrompts(prev => {
      const list = Array.isArray(prev) ? [...prev] : normalizePrompts(prev);
      list[index] = { ...list[index], [key]: value };
      return list;
    });
    setDirty(true);
  };

  const handleRemove = (index) => {
    setPrompts(prev => {
      const list = Array.isArray(prev) ? prev : normalizePrompts(prev);
      return list.filter((_, i) => i !== index);
    });
    setDirty(true);
  };

  const handleSaveClick = async () => {
    const result = await onSave(safePrompts);
    if (result) { setPrompts(normalizePrompts(result)); setDirty(false); }
  };

  const getRegionColumns = (regionId) => safeColumns.filter(c => c.region_id === regionId);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#495057' }}>AI Prompts ({safePrompts.length})</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleAdd} style={btnOutline}>+ Add Prompt</button>
          <button type="button" onClick={handleSaveClick} disabled={!dirty} style={{ ...btnPrimary, opacity: dirty ? 1 : 0.5 }}>Save Prompts</button>
        </div>
      </div>

      {safePrompts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#6c757d' }}>
          No prompts defined. Add a prompt for each region to configure AI extraction.
        </div>
      ) : (
        safePrompts.map((p, i) => {
          const regionCols = getRegionColumns(p.region_id);
          const regionObj = safeRegions.find(r => r.id === p.region_id);
          return (
            <div key={i} style={{ border: '1px solid #e9ecef', borderRadius: 8, padding: 16, marginBottom: 12, backgroundColor: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  Prompt {i + 1}{regionObj ? ` — ${regionObj.region_label || regionObj.region_name}` : ''}
                </span>
                <button type="button" onClick={() => handleRemove(i)} style={delBtn}>Remove</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Region</label>
                  <select
                    value={p.region_id != null ? String(p.region_id) : ''}
                    onChange={e => handleChange(i, 'region_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                    style={inputStyle}
                  >
                    <option value="">— None —</option>
                    {safeRegions.map((r, ri) => {
                      const rid = r.id != null ? r.id : ri + 1;
                      return <option key={rid} value={String(rid)}>{r.region_label || r.region_name || `Region ${ri + 1}`}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>AI Model</label>
                  <select value={p.ai_model || 'gpt-4o'} onChange={e => handleChange(i, 'ai_model', e.target.value)} style={inputStyle}>
                    {AI_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!p.use_auto_format} onChange={e => handleChange(i, 'use_auto_format', e.target.checked)} />
                    Auto-format
                  </label>
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Prompt Template
                  {regionCols.length > 0 && (
                    <span style={{ fontWeight: 400, color: '#6c757d' }}>
                      {' '}— Available: {regionCols.map(c => `{{${c.column_key || c.key}}}`).join(', ')}
                    </span>
                  )}
                </label>
                <textarea
                  value={p.prompt_template || ''}
                  onChange={e => handleChange(i, 'prompt_template', e.target.value)}
                  rows={6}
                  placeholder="Enter the AI prompt template for extracting data from this region..."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const btnPrimary = { padding: '8px 16px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnOutline = { padding: '8px 16px', backgroundColor: '#fff', color: '#0d6efd', border: '1px solid #0d6efd', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500, color: '#495057' };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' };
const delBtn = { padding: '4px 10px', fontSize: 12, border: '1px solid #dc3545', borderRadius: 4, backgroundColor: '#fff', color: '#dc3545', cursor: 'pointer' };

export default FormatPromptsEditor;
